import { createContext, PropsWithChildren, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { BoltConnectionUpdateDto } from '../../_generated/api/structure'
import Bolt from '../../models/Bolt'
import BoltConnection, { BoltDetails } from '../../models/BoltConnection'
import DataContextValue from './DataContextValue'
import { useApiContext } from './ApiContext'
import { useUserContext } from './UserContext'
import { useStructureContext } from './StructureContext'
import { useToastContext } from './ToastContext'
import { useProvideContext } from '../../hooks/context'
import { useMutableObject } from '../../hooks/mutable'
import { useImmutableObject } from '../../hooks/immutable'
import { usePaginatedTable } from '../../hooks/pagination'
import { useAction } from '../../hooks/action'
import { useQR } from '../../hooks/qr'
import { mapReadTableResponse, mapReadResponse, throwIfResponseNotOk } from '../../tools/api'
import { StructureTypes } from '../../models/Structure'
import TableControl from '../../models/TableControl'
import Pagination from '../../models/Pagination'
import { unpackBoltConnectionBoltsTableSpec } from '../table/table-spec'
import { useArchiveContext } from './ArchiveContext'
import { useDateRangeState } from '../../hooks/state'

export interface BoltConnectionContextValue extends DataContextValue {
    qr?: string
    qrLoading: boolean
    qrError: Error | undefined

    selectedBoltConnection?: BoltConnection
    selectedBoltConnectionLoading: boolean
    selectedBoltConnectionError: Error | undefined
    bolts: TableControl<Bolt>
    allBolts: TableControl<Bolt>

    refreshAllBolts: () => Promise<void>
    refreshBolts: () => Promise<void>
    refreshSelectedBoltConnection: () => Promise<void>
    unassignBolt: (bolt: Bolt) => Promise<void>
    updateBoltConnection: (change: BoltConnectionUpdateDto) => Promise<BoltConnection | undefined>

    boltDetails?: BoltDetails
    pretensionDateRange: (Date | null)[]
    setPretensionDateRange: (dateRange: Date[]) => void
}

const BoltConnectionContext = createContext<BoltConnectionContextValue | undefined>(undefined)

export default function ({ children }: PropsWithChildren) {
    const { boltConnectionId } = useParams()
    const { boltConnectionApi, boltApi } = useApiContext()
    const { includeArchived } = useArchiveContext()
    const { selectedOrganization } = useUserContext()
    const { updateStructureByImpactDto } = useStructureContext()
    const Toast = useToastContext()

    const [pretensionStart, pretensionEnd, setPretensionDateRange] = useDateRangeState()
    const pretensionDateRange = [pretensionStart, pretensionEnd]?.filter((d) => !!d)

    const [
        selectedBoltConnection,
        selectedBoltConnectionLoading,
        selectedBoltConnectionError,
        refreshSelectedBoltConnection,
    ] = useMutableObject(async () => {
        const orgId = selectedOrganization?.id
        if (!(orgId && boltConnectionId)) return
        const id = boltConnectionId
        const response = await boltConnectionApi.getBoltConnectionV3({
            boltConnectionId: id,
            tenantId: orgId,
            includeArchived: true,
        })
        return mapReadResponse(response, BoltConnection)
    }, [boltConnectionId, selectedOrganization?.id])

    const [boltDetails, _boltDetailsLoading, _boltDetailsError] = useImmutableObject(async () => {
        const orgId = selectedOrganization?.id
        if (!(orgId && boltConnectionId)) return null
        const response = await boltApi.getBoltConnectionBoltDetailsV2({
            boltConnectionId: boltConnectionId,
            tenantId: orgId,
            measuredAtStart: pretensionStart?.toISOString(),
            measuredAtEnd: pretensionEnd?.toISOString(),
        })
        return mapReadResponse(response, BoltDetails)
    }, [boltConnectionId, pretensionStart, pretensionEnd, selectedOrganization?.id])

    const [allBolts, _allBoltsLoading, _allBoltsError, refreshAllBolts] = usePaginatedTable(
        async (table: TableControl<Bolt>) => {
            const orgId = selectedOrganization?.id
            if (!(orgId && boltConnectionId)) return null
            // We use infinite pagination for the bolts of a connection,
            //  because we need to display all of them in the graph - even when the table is filtered.
            const { skip, take } = Pagination.infinite
            const response = await boltApi.getBoltsOfBoltConnectionV2({
                boltConnectionId,
                tenantId: orgId,
                skip,
                take,
                includeMeasurements: true,
            })
            return mapReadTableResponse(response, table, Bolt)
        },
        [boltConnectionId, selectedOrganization?.id]
    )

    const [bolts, boltsLoading, boltsError, refreshBolts] = usePaginatedTable(
        async (table: TableControl<Bolt>) => {
            const orgId = selectedOrganization?.id
            if (!(orgId && boltConnectionId)) return null
            const { diameter, type, material, nominalLength, createdAt } =
                unpackBoltConnectionBoltsTableSpec(table)
            const response = await boltApi.getBoltsOfBoltConnectionV2({
                boltConnectionId,
                tenantId: orgId,
                sortBy: table.sortBy,
                ascending: table.ascending,
                createdAtStart: createdAt?.[0]?.toISOString(),
                createdAtEnd: createdAt?.[1]?.toISOString(),
                nominalLengthStart: nominalLength?.[0],
                nominalLengthEnd: nominalLength?.[1],
                boltDiameter: diameter?.[0],
                material: material?.[0],
                boltType: type?.[0],
                skip: table.skip,
                take: table.take,
                includeArchived,
                includeMeasurements: true,
            })

            return mapReadTableResponse(response, table, Bolt)
        },
        [boltConnectionId, selectedOrganization?.id, includeArchived]
    )

    const [_unassignLoading, unassignError, unassignBolt] = useAction(async (bolt: Bolt) => {
        if (!confirm('Are you sure you want to unassign bolt:\n' + bolt.name)) return
        const orgId = selectedOrganization?.id
        if (!orgId) return
        const response = await boltApi.unassignBoltV2({ boltId: bolt.id, tenantId: orgId })
        throwIfResponseNotOk(response)
        refreshAllBolts()
        refreshBolts()
        Toast.completed('unassign', 'bolt')
    })

    const [_updateLoading, updateError, updateBoltConnection] = useAction(
        async (change: BoltConnectionUpdateDto) => {
            const orgId = selectedOrganization?.id
            if (!(orgId && boltConnectionId)) return
            const response = await boltConnectionApi.updateBoltConnectionV3({
                boltConnectionId,
                tenantId: orgId,
                boltConnectionUpdateDto: change,
            })
            throwIfResponseNotOk(response)
            updateStructureByImpactDto(response.data)
            refreshSelectedBoltConnection()
            refreshBolts()
            return new BoltConnection(response.data.model)
        }
    )

    const [qr, qrLoading, qrError] = useQR(
        StructureTypes.BoltConnection,
        selectedBoltConnection?.id
    )

    const loading = boltsLoading
    const error = boltsError || updateError || unassignError

    useEffect(() => {
        if (error) Toast.error(error, 'Bolt Connection error')
    }, [error])

    const value: BoltConnectionContextValue = {
        loading,
        error,

        qr,
        qrLoading,
        qrError,

        selectedBoltConnection,
        selectedBoltConnectionLoading,
        selectedBoltConnectionError,
        bolts,
        allBolts,

        refreshAllBolts,
        refreshBolts,
        refreshSelectedBoltConnection,
        unassignBolt,
        updateBoltConnection,

        boltDetails,
        pretensionDateRange,
        setPretensionDateRange,
    }

    return <BoltConnectionContext.Provider value={value}>{children}</BoltConnectionContext.Provider>
}

export const useBoltConnectionContext = () =>
    useProvideContext(BoltConnectionContext, 'BoltConnection')
